import { Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import type { ReturnLinkProps } from './types';

/**
 * `← Back to <somewhere>`.
 *
 * **A link to a known place, not `history.back()`.** The two differ whenever a
 * page is arrived at directly — from a bookmark, a shared URL, a browser reload
 * — where going back leaves the app entirely. The caller knows the parent; the
 * browser only knows the previous page.
 *
 * **The arrow is supplied here.** It is a literal character inside a Figma text
 * node, so it looks like content and is not: the API returns clean strings.
 * Every glyph in the designs — `←`, `•`, `↗`, `›` — belongs to the component
 * that draws it, and a label arriving with one already in it is a bug upstream.
 */
export const ReturnLink = ({ to, label }: ReturnLinkProps) => (
  <RouterLink to={to} style={{ textDecoration: 'none' }}>
    <Typography
      variant="body2"
      component="span"
      sx={{
        color: 'text.link',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {/* aria-hidden: the arrow is decoration, and "Back to" already says which
          direction this goes. A screen reader announcing "left arrow" does not
          help. */}
      <span aria-hidden>&#8592;</span>
      Back to {label}
    </Typography>
  </RouterLink>
);
